import { forwardRef, useCallback, useEffect, type FC } from 'react';
import {
  AppBar,
  Box,
  CircularProgress,
  Container,
  Dialog,
  Grid,
  IconButton,
  Slide,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { type Audit } from '#packages/data-collection/models/audit';
import { type MutationPageState } from '#packages/data-collection/types/page-state';
import {
  type AuditSyncState,
  type FormLoadState,
  type FormSyncState,
} from '#apps/client-web/features/sync/hooks/use-sync-audit-mutation';
import { useAuth } from '#apps/client-web/features/auth/hooks/use-auth';

export type AuditDetailsSyncDialogProps = {
  audit: Audit;
  syncState: AuditSyncState;
  syncMutationState: MutationPageState;
  isSyncSuccess: boolean;
  resetSyncState: () => void;
  onClose: () => void;
  onSync: () => void;
};

const renderSyncIcon = (state: FormLoadState | FormSyncState) => {
  let IconComponent;

  switch (state) {
    case 'error': {
      IconComponent = CancelOutlinedIcon;
      break;
    }
    case 'success': {
      IconComponent = CheckCircleOutlinedIcon;
      break;
    }
    case 'idle': {
      IconComponent = CheckBoxOutlineBlankIcon;
      break;
    }
    case 'loading':
    case 'pending': {
      IconComponent = SyncOutlinedIcon;
      break;
    }
  }

  return <IconComponent color="inherit" />;
};

const Transition = forwardRef(function Transition(
  props: React.ComponentProps<typeof Slide>,
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" enter exit ref={ref} {...props} />;
});

export const AuditDetailsSyncDialog: FC<AuditDetailsSyncDialogProps> = ({
  audit,
  syncState,
  syncMutationState,
  isSyncSuccess,
  resetSyncState,
  onClose,
  onSync,
  ...props
}) => {
  const isPending = syncMutationState.state === 'pending';
  const isError = syncMutationState.state === 'error';
  const isSuccess = syncMutationState.state === 'success';
  const { isSignedIn } = useAuth();
  const theme = useTheme();

  const getItemColor = useCallback(
    (state: FormLoadState | FormSyncState) => {
      switch (state) {
        case 'error': {
          return theme.palette.error.main;
        }
        case 'success': {
          return theme.palette.success.main;
        }
        case 'idle': {
          return theme.palette.text.primary;
        }
        case 'loading':
        case 'pending': {
          return theme.palette.info.main;
        }
      }
    },
    [theme],
  );

  useEffect(() => {
    if (isSignedIn) {
      onSync();
    }

    return () => resetSyncState();
  }, [isSignedIn]);

  return (
    <Dialog
      open
      fullScreen
      maxWidth="sm"
      slots={{
        transition: Transition,
      }}
      {...props}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close">
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Synchronize Audit
          </Typography>
          {isSignedIn &&
            (isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <IconButton
                edge="end"
                color="inherit"
                disabled={isPending}
                onClick={isSuccess ? onClose : onSync}>
                <Check color="inherit" />
              </IconButton>
            ))}
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" sx={{ height: '100%' }}>
        {isSignedIn ? (
          <Box sx={{ py: 3 }}>
            <Typography variant="body2" color="textSecondary">
              {syncMutationState.state === 'success'
                ? 'Synchronized:'
                : 'Synchronizing:'}
            </Typography>
            <Typography variant="body1">{audit.title}</Typography>

            <Box sx={{ mt: 3 }} />

            <Stack spacing={2}>
              <Grid
                container
                spacing={1}
                sx={{ color: getItemColor(syncState.preparing) }}>
                {renderSyncIcon(syncState.preparing)}
                <Typography variant="body1">Preparing forms</Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{ color: getItemColor(syncState.forms.mechanical) }}>
                {renderSyncIcon(syncState.forms.mechanical)}
                <Typography variant="body1">
                  Uploading Mechanical form data
                </Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{
                  color: getItemColor(syncState.attachments.mechanical),
                }}>
                {renderSyncIcon(syncState.attachments.mechanical)}
                <Typography variant="body1">
                  Uploading Mechanical form attachments
                </Typography>
              </Grid>
            </Stack>

            {isError && (
              <Typography color="error" variant="subtitle1" sx={{ mt: 4 }}>
                {syncMutationState.error instanceof Error
                  ? syncMutationState.error.message
                  : 'Unknown error'}
              </Typography>
            )}
          </Box>
        ) : (
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%' }}>
            <Box>
              <Typography
                color="textPrimary"
                textAlign="center"
                variant="h6"
                sx={{ mb: 1 }}>
                Sign in needed
              </Typography>
              <Typography
                color="textSecondary"
                textAlign="center"
                variant="subtitle1">
                To be able to upload your audit data, you need to sign in first.
                Please do so from the menu on the main page.
              </Typography>
            </Box>
          </Grid>
        )}
      </Container>
    </Dialog>
  );
};
